import React, { Fragment, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as S from './SearchResults.styles';
import { SearchResultsProps } from './SearchResults.types';
import { useOrderChange } from './hooks/useOrderChange';
import { useSearchResultsData } from './hooks/useSearchResultsData';
import { useSearchResultsLoadMore } from './hooks/useSearchResultsLoadMore';
import { AdsSection } from '@/components/AdsSection/AdsSection';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { AdTransitionSource } from '@/api/types/ads.types';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import { VipAndLatestAdsSkeletonLoader } from '@/components/AdsWithInfiniteScroll/VipAndLatestAdsSkeletonLoader';
import { EmptySearchResults } from '@/components/EmptySearchResults/EmptySearchResults';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { useServerDataContext } from '@/contexts';
import { DesktopFilters } from '@/components/Filters/DesktopFilters';
import { useResponsive } from '@/hooks/useResponsive';
import { useBanner } from '@/hooks/useBanner';
import { SortSelector } from '@/components/Filters/SortSelector';
import { useParseQuery } from '@/hooks/useParseQuery';
import NoSsr from '@/components/NoSsr/NoSsr';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import type { AdsSectionLayoutScope } from '@/components/AdsSection/AdsSection.types';
import { KeywordsAdsSection } from '@/components/SearchResults/KeywordsAdsSection';
import { AdsCounter } from '@/components/Filters/AdsCounter';
import { PhoneNumberSearch } from '@/features/PhoneNumberSearch';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { buildVipAdsAllHref } from '@/components/SearchResults/hooks/useSearchResultsData.helpers';

const DEFAULT_SECTIONS = { vipAds: true, latestAds: true };

const SearchResultsInner: React.FC<SearchResultsProps> = ({
  sections = DEFAULT_SECTIONS,
  onPaginationChange,
}) => {
  const { t } = useTranslation();
  const { currentCategory: serverCurrentCategory } = useServerDataContext();
  const {
    currentCategory = serverCurrentCategory,
    isAllCategories,
    isFirstLevel,
    isSubcategories,
  } = useCurrentCategory();
  const { isDesktop } = useResponsive();
  const { order, keywords } = useParseQuery();
  const handleChangeOrder = useOrderChange();
  const { inListBanner, inListDesktopBanner, bottomBanner } = useBanner({
    isDesktop,
  });
  const {
    chunkedVipAds,
    chunkedLatestAds,
    isLoading,
    hasNextPage,
    fetchMoreVipAds,
    fetchMoreLatestAds,
    vipChunkSize,
    showVip,
    showLatest,
    keywordsAds,
    viewAllResultsHref,
    prunedPropertyOptions,
  } = useSearchResultsData({ sections, onPaginationChange });
  const { isLoadingMore, onLoadMore } = useSearchResultsLoadMore({
    isLoading,
    hasNextPage,
    fetchMoreVipAds,
    fetchMoreLatestAds,
  });
  const { queryString } = useUrlAndHashFromPath();

  const allAdsLinkData = useMemo(
    () => ({
      href: buildVipAdsAllHref(currentCategory?.path, queryString),
      className: 'section-title_more',
    }),
    [currentCategory?.path, queryString]
  );

  function getLayoutScope(): AdsSectionLayoutScope {
    if (isSubcategories) return 'subcategory';
    if (isFirstLevel) return 'category';
    if (isAllCategories) return 'all-categories';

    return 'all-categories';
  }

  const layoutScope = getLayoutScope();

  const renderCards = () => {
    if (!currentCategory?.id) {
      return (
        <VipAndLatestAdsSkeletonLoader
          sections={sections}
          layoutScope={layoutScope}
        />
      );
    }

    const hasLatest = showLatest
      ? chunkedLatestAds.flat().some(Boolean)
      : chunkedVipAds.flat().some(Boolean);
    const hasKeywordsAds = keywordsAds.length > 0;

    if (!hasLatest && !hasKeywordsAds) {
      if (isLoading) {
        return (
          <VipAndLatestAdsSkeletonLoader
            sections={sections}
            layoutScope={layoutScope}
          />
        );
      }

      return <EmptySearchResults />;
    }

    if (!hasLatest && hasKeywordsAds) {
      return (
        <>
          <S.NoResultsInCategoryText>
            <Trans
              i18nKey="pages.ads.other_results_for_query"
              values={{ query: keywords }}
              components={{ bold: <strong /> }}
            />
          </S.NoResultsInCategoryText>
          <KeywordsAdsSection
            ads={keywordsAds}
            viewAllResultsHref={viewAllResultsHref}
            title={t('pages.ads.other_categories_results')}
          />
        </>
      );
    }

    if (showVip && !showLatest) {
      if (isLoading) {
        return (
          <VipAndLatestAdsSkeletonLoader
            sections={sections}
            layoutScope={layoutScope}
          />
        );
      }

      return (
        <S.AdsListContainer $isVipPage>
          {chunkedVipAds.map((latestChunk, i) => {
            const isFirstChunk = !i;
            const vipAds = latestChunk;
            return (
              <AdsSection
                key={`vip_${i}`}
                index={i}
                ads={vipAds}
                hasPaddingTop={isFirstChunk}
                chunkSize={vipChunkSize}
                dataTestId={SearchResultsPageTestIds.vipAdsSection}
                adTransitionSource={AdTransitionSource.Vip}
                isBannerInListAfterThreeRows={!isDesktop}
                isBannerInListAtTheEnd={!isDesktop}
                bottomBanner={bottomBanner}
                bannerInList={inListBanner}
                layoutScope={layoutScope}
              />
            );
          })}
          {isLoadingMore ? (
            <AdsSection
              noPadding
              adsLoading
              chunkSize={vipChunkSize}
              layoutScope={layoutScope}
            />
          ) : (
            <InfiniteLoader onVisit={onLoadMore} rootMargin={200} />
          )}
        </S.AdsListContainer>
      );
    }

    return (
      <>
        {chunkedLatestAds.map((latestChunk, i) => {
          const isFirstChunk = !i;
          const vipAds = showVip ? chunkedVipAds[i] : [];
          const latestAds = latestChunk;
          const hasVipAds = vipAds && vipAds.length > 0;
          const latestAdsTitle =
            isFirstChunk || hasVipAds ? t('pages.ads.ads') : undefined;

          return (
            <Fragment key={`vip_and_latest_${i}`}>
              {showVip && hasVipAds && (
                <AdsSection
                  title={t('pages.ads.vip_ads')}
                  ads={vipAds}
                  hasPaddingTop={isFirstChunk}
                  dataTestId={SearchResultsPageTestIds.vipAdsSection}
                  hasFirstCardPriority
                  allAdsLinkData={allAdsLinkData}
                  adTransitionSource={AdTransitionSource.Vip}
                  layoutScope={layoutScope}
                />
              )}
              {!!latestAds?.length && (
                <AdsSection
                  ads={latestAds}
                  hasPaddingTop={isFirstChunk && !hasVipAds}
                  title={latestAdsTitle}
                  index={i}
                  bannerInList={inListBanner}
                  inListDesktopBanner={inListDesktopBanner}
                  bottomBanner={bottomBanner}
                  isBannerInListAtTheEnd
                  isBannerInListAfterThreeRows
                  dataTestId={SearchResultsPageTestIds.latestAdsSection}
                  adTransitionSource={AdTransitionSource.SearchResults}
                  layoutScope={layoutScope}
                />
              )}
            </Fragment>
          );
        })}

        {hasKeywordsAds && (
          <KeywordsAdsSection
            ads={keywordsAds}
            viewAllResultsHref={viewAllResultsHref}
            title={t('pages.ads.other_categories_results')}
          />
        )}

        {isLoadingMore ? (
          <>
            <SkeletonLoader
              height={29}
              width={180}
              borderRadius={14}
              style={{ marginBottom: 20, marginTop: 5 }}
            />
            <AdsSection
              noPadding
              adsLoading
              chunkSize={AdsChunkSize.Compact}
              layoutScope={layoutScope}
            />
          </>
        ) : (
          <InfiniteLoader onVisit={onLoadMore} rootMargin={200} />
        )}
      </>
    );
  };

  if (isDesktop && showLatest) {
    const ads = chunkedLatestAds.flat().filter(Boolean);

    return (
      <S.Wrapper>
        <DesktopFilters withOverflow={ads.length <= 8} />
        <S.AdsListContainer>
          <PhoneNumberSearch />
          <NoSsr
            fallback={
              <S.AdsListToolbar>
                <SkeletonLoader width={100} height={18} borderRadius={7} />
                <SkeletonLoader width={173} height={40} borderRadius={20} />
              </S.AdsListToolbar>
            }
          >
            <S.AdsListToolbar>
              <AdsCounter propertyOptions={prunedPropertyOptions} />
              <SortSelector value={order} onChange={handleChangeOrder} />
            </S.AdsListToolbar>
          </NoSsr>
          {renderCards()}
        </S.AdsListContainer>
      </S.Wrapper>
    );
  }

  return renderCards();
};

export const SearchResults = React.memo(SearchResultsInner);
